/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as auth from "../redux/AuthRedux";
import { login } from "../redux/AuthCRUD";
import { setShowSessionExpired } from "app/system/sessionExpired/SessionExpiredSlice";
import CPLPasswordInput from "cpl-react-common/components/Inputs/CPLPasswordInput";

const loginSchema = Yup.object().shape({
  email: Yup.string().email("Wrong email format").required("Email is required"),
  password: Yup.string().required("Password is required"),
});

const initialValues = {
  email: "",
  password: "",
};

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={loginSchema}
      onSubmit={(values, { setStatus, setSubmitting }) => {
        setLoading(true);
        login(values.email, values.password)
          .then((tokens) => {
            setLoading(false);
            dispatch(auth.actions.login(tokens));
            dispatch(setShowSessionExpired(false));
          })
          .catch(({ message }: { message: string }) => {
            if (message.includes("NetworkError")) {
              setStatus("Unable to connect to server");
            } else {
              setStatus("Your password is incorrect or this account doesn't exist");
            }

            setLoading(false);
            setSubmitting(false);
          });
      }}
    >
      {({ values, touched, status, errors, isSubmitting, getFieldProps, isValid }) => (
        <Form className="form w-100" noValidate id="kt_login_signin_form">
          {/* begin::Heading */}
          <div className="text-center mb-10">
            <h1 className="text-dark mb-3">Sign in to Proptimo</h1>
          </div>
          {/* begin::Heading */}
          {status && <div className="alert alert-danger">{status}</div>}
          {/* begin::Form group */}
          <div className="fv-row mb-10">
            <label className="form-label fs-6 fw-bolder text-dark">Email</label>
            <Field
              data-test-id="login-form__email-input"
              placeholder="Email"
              {...getFieldProps("email")}
              className={clsx(
                "form-control form-control-lg form-control-solid",
                { "is-invalid": touched.email && errors.email },
                {
                  "is-valid": touched.email && !errors.email,
                }
              )}
              type="email"
              autoComplete="off"
            />
            <div className="fv-plugins-message-container">
              <div className="fv-help-block">
                <ErrorMessage name="email" />
              </div>
            </div>
          </div>
          {/* end::Form group */}

          {/* begin::Form group */}
          <div className="fv-row mb-10">
            <div className="d-flex justify-content-between mt-n5">
              <div className="d-flex flex-stack mb-2">
                {/* begin::Label */}
                <label className="form-label fw-bolder text-dark fs-6 mb-0">Password</label>
                {/* end::Label */}
              </div>
            </div>
            <CPLPasswordInput data-test-id="login-form__password-input" id="password" getFieldProps={getFieldProps} />
            {/* begin::Link */}
            <Link
              data-test-id="login-form__forgot-password-link"
              to={`/auth/forgot-password?email=${values.email}`}
              className="d-inline-block link-primary fs-7 fw-bold mt-4"
            >
              Forgot Password?
            </Link>
            {/* end::Link */}
          </div>
          {/* end::Form group */}

          {/* begin::Action */}
          <div className="text-center">
            <button
              data-test-id="login-form__submit-button"
              type="submit"
              id="kt_sign_in_submit"
              className="btn btn-lg btn-primary w-100 mb-5"
              disabled={isSubmitting || !isValid}
            >
              {!loading && <span className="indicator-label">Continue</span>}
              {loading && (
                <span className="indicator-progress" style={{ display: "block" }}>
                  Please wait...
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              )}
            </button>
          </div>
          {/* end::Action */}
        </Form>
      )}
    </Formik>
  );
}
